import { useEffect, useState } from "react";
import { Check, Copy, Gift, Share2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const LINE = "Watch this — ChatMiLu in 60 seconds. Chat, message and read the news in 50 languages.";

// A member hands the tour to a friend. Both of them get free Premium days when the friend joins.
export const ShareTour = () => {
  const { user } = useAuth();
  const [card, setCard] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.get("/tour/share").then(({ data }) => setCard(data))
      .catch((e) => console.error("could not load the tour share card", e));
  }, [user]);

  if (!user || !card?.link) return null;

  const seen = () => {
    api.post("/tour/shared", { channel: "link" }).catch(() => {});
    setCard((was) => (was ? { ...was, shares: (was.shares || 0) + 1 } : was));
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(`${LINE} ${card.link}`);
      setCopied(true);
      seen();
      toast.success("Your tour link is copied — send it to a friend");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Your browser would not let me copy that");
    }
  };

  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "The ChatMiLu tour", text: LINE, url: card.link });
        seen();
      } catch {
        // the sheet was dismissed, nothing to say
      }
      return;
    }
    copy();
  };

  const open = (url) => {
    seen();
    window.open(url, "_blank", "noopener,width=640,height=680");
  };

  return (
    <div className="rounded-3xl border border-white/10 bg-panel p-6" data-testid="share-tour">
      <p className="flex items-center gap-2 text-base font-semibold">
        <Gift className="h-4 w-4 text-brand" /> Send the tour to a friend
      </p>
      <p className="mt-1 text-sm text-ink-soft">
        They watch the 60 seconds first, and when they join you both get free Premium days.
      </p>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <span
          data-testid="share-tour-link"
          className="max-w-full truncate rounded-full border border-white/10 bg-page px-4 py-2 text-xs text-ink-soft"
        >
          {card.link}
        </span>
        <button
          data-testid="share-tour-share"
          onClick={share}
          className="inline-flex min-h-[40px] items-center gap-2 rounded-full bg-brand px-5 text-sm font-semibold text-white transition-transform hover:bg-brand-hover active:scale-95"
        >
          <Share2 className="h-4 w-4" /> Share the tour
        </button>
        <button
          data-testid="share-tour-copy"
          onClick={copy}
          className="inline-flex min-h-[40px] items-center gap-2 rounded-full border border-white/15 px-4 text-xs text-ink-soft transition-colors hover:border-brand/50 hover:text-brand"
        >
          {copied ? <Check className="h-3.5 w-3.5 text-forest" /> : <Copy className="h-3.5 w-3.5" />}
          Copy link
        </button>
        <button
          data-testid="share-tour-whatsapp"
          onClick={() => open(`https://wa.me/?text=${encodeURIComponent(`${LINE} ${card.link}`)}`)}
          className="min-h-[40px] rounded-full border border-white/15 px-4 text-xs text-ink-soft transition-colors hover:border-brand/50 hover:text-brand"
        >
          WhatsApp
        </button>
        <button
          data-testid="share-tour-x"
          onClick={() => open(
            `https://twitter.com/intent/tweet?text=${encodeURIComponent(LINE)}&url=${encodeURIComponent(card.link)}`)}
          className="min-h-[40px] rounded-full border border-white/15 px-4 text-xs text-ink-soft transition-colors hover:border-brand/50 hover:text-brand"
        >
          Post on X
        </button>
      </div>

      <p className="mt-4 text-sm text-ink-soft" data-testid="share-tour-stats">
        {card.watched} friend{card.watched === 1 ? "" : "s"} watched your link ·{" "}
        {card.joined} joined
        {card.upgraded ? ` · ${card.upgraded} went premium` : ""}
      </p>
    </div>
  );
};
